import {TFunction} from 'i18next';
import * as XLSX from 'xlsx';
import moment from 'moment';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {UserGender} from '../../../../backend/enums';
import {translations} from '../../../../i18n/translations';
import {joinAsExpression} from '../../../functions/string';
import {buildExportFileName} from '../base/export-file-name';
import type {PreparedXlsxExportFile} from '../base/export-data-types';

export function prepareProfessionalCaregiversListXLSX({
  reportData,
  entity,
  t,
}: {
  reportData: any[];
  entity?: EntityInfo;
  t: TFunction;
}): PreparedXlsxExportFile {
  const rows: string[][] = [];

  for (const entry of reportData) {
    const user = entry.userId;
    const name = user.displayName ?? user.name ?? '';
    const fullName = user.name ?? '';
    const age = user.birthday ? moment().diff(user.birthday, 'years').toString() : '';
    const gender = user.gender ? t(translations.genders[user.gender as UserGender]) : '';
    const taxPayerNumber = user.taxPayerNumber?.toString() ?? '';
    const occupation = user.occupationId ?? '';
    const roles = entry.rolesIds?.length ? joinAsExpression(entry.rolesIds) : '';
    const phone = user.phoneNumber ?? '';
    const email = user.email ?? '';
    const address = user.address ?? '';
    const region = user.region ?? '';
    const language = user.languageCode ?? '';
    const status = user.isActive
      ? t(translations.professional_caregivers.root.state_value_true)
      : t(translations.professional_caregivers.root.state_value_false);

    rows.push([
      name,
      fullName,
      age,
      gender,
      taxPayerNumber,
      occupation,
      roles,
      phone,
      email,
      address,
      region,
      language,
      status,
    ]);
  }

  const nameStr = t(translations.professional_caregivers.root.name_header);
  const fullNameStr = t(translations.user_form.full_name_input);
  const ageStr = t(translations.office.professional_caregiver.seniors.table_view_screen.age);
  const genderStr = t(translations.seniors.root.gender_header);
  const taxPayerStr = t(translations.user_form.tax_payer_number_input);
  const occupationStr = t(translations.user_form.profession_input);
  const rolesStr = t(translations.user_form.roles_ids_input);
  const phoneStr = t(translations.user_form.mobile_phone_number_input);
  const emailStr = t(translations.user_form.email_input);
  const addressStr = t(translations.user_form.address_input);
  const regionStr = t(translations.user_form.region_input);
  const languageStr = t(translations.user_form.language_input);
  const statusStr = t(translations.professional_caregivers.root.state_header);

  const workbook = XLSX.utils.book_new();
  let worksheet = XLSX.utils.json_to_sheet(rows);

  worksheet = XLSX.utils.sheet_add_aoa(worksheet, [
    [
      nameStr,
      fullNameStr,
      ageStr,
      genderStr,
      taxPayerStr,
      occupationStr,
      rolesStr,
      phoneStr,
      emailStr,
      addressStr,
      regionStr,
      languageStr,
      statusStr,
    ],
  ]);

  worksheet['!cols'] = [
    {wch: 25},
    {wch: 30},
    {wch: 10},
    {wch: 15},
    {wch: 40},
    {wch: 45},
    {wch: 45},
    {wch: 20},
    {wch: 30},
    {wch: 30},
    {wch: 15},
    {wch: 15},
    {wch: 15},
    {wch: 15},
  ];

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    t(translations.professional_caregivers.root.title)
  );

  const fileName = buildExportFileName([
    entity?.shortName ?? entity?.name,
    t(translations.professional_caregivers.root.title),
  ]);

  return {
    file: workbook,
    fileName,
  };
}
